'use client';

import { useEffect, useState } from 'react';

import type { Task } from '@/features/tasks/types';

export type PersistedTimerStatus = 'idle' | 'running' | 'paused' | 'confirming';
export type PersistedTrackerMode = 'timer' | 'manual';
export type PersistedTimeFormat = 'clock' | 'decimal';

export type PersistedTimeTrackerState = {
  userId: string;
  workspaceId: string;
  selectedTask: Task | null;
  mode: PersistedTrackerMode;
  timeFormat: PersistedTimeFormat;
  entryDate: string | null;
  note: string;
  manualValue: string;
  elapsedSeconds: number;
  timerStatus: PersistedTimerStatus;
  updatedAt: number;
};

export const TIME_TRACKER_STORAGE_PREFIX = 'netnet:time-tracker';
export const TIME_TRACKER_DISCARD_EVENT = 'netnet:time-tracker:discard';
export const TIME_TRACKER_CHANGE_EVENT = 'netnet:time-tracker:change';

function canUseStorage() {
  return (
    typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
  );
}

function emitTimeTrackerChange() {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new Event(TIME_TRACKER_CHANGE_EVENT));
}

export function getTimeTrackerStorageKey(
  userId?: string,
  workspaceId?: string,
) {
  if (!userId || !workspaceId) {
    return null;
  }

  return `${TIME_TRACKER_STORAGE_PREFIX}:${userId}:${workspaceId}`;
}

function parsePersistedState(value: string | null) {
  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value) as PersistedTimeTrackerState;
  } catch {
    return null;
  }
}

export function loadPersistedTimeTracker(
  userId?: string,
  workspaceId?: string,
) {
  if (!canUseStorage()) {
    return null;
  }

  const key = getTimeTrackerStorageKey(userId, workspaceId);
  if (!key) {
    return null;
  }

  const state = parsePersistedState(window.localStorage.getItem(key));
  if (!state || state.userId !== userId || state.workspaceId !== workspaceId) {
    return null;
  }

  return state;
}

export function savePersistedTimeTracker(state: PersistedTimeTrackerState) {
  if (!canUseStorage()) {
    return;
  }

  const key = getTimeTrackerStorageKey(state.userId, state.workspaceId);
  if (!key) {
    return;
  }

  window.localStorage.setItem(key, JSON.stringify(state));
  emitTimeTrackerChange();
}

export function clearPersistedTimeTracker(
  userId?: string,
  workspaceId?: string,
) {
  if (!canUseStorage()) {
    return;
  }

  const key = getTimeTrackerStorageKey(userId, workspaceId);
  if (key) {
    window.localStorage.removeItem(key);
    emitTimeTrackerChange();
  }
}

export function clearPersistedTimeTrackersForWorkspace(workspaceId?: string) {
  if (!canUseStorage() || !workspaceId) {
    return;
  }

  let didClear = false;

  Object.keys(window.localStorage)
    .filter((key) => key.startsWith(`${TIME_TRACKER_STORAGE_PREFIX}:`))
    .forEach((key) => {
      const state = parsePersistedState(window.localStorage.getItem(key));
      if (state?.workspaceId === workspaceId) {
        window.localStorage.removeItem(key);
        didClear = true;
      }
    });

  if (didClear) {
    emitTimeTrackerChange();
  }
}

export function discardPersistedTimeTracker(workspaceId?: string) {
  clearPersistedTimeTrackersForWorkspace(workspaceId);

  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(TIME_TRACKER_DISCARD_EVENT, {
      detail: { workspaceId },
    }),
  );
}

export function hasActivePersistedTimeTracker(workspaceId?: string) {
  if (!canUseStorage() || !workspaceId) {
    return false;
  }

  return Object.keys(window.localStorage)
    .filter((key) => key.startsWith(`${TIME_TRACKER_STORAGE_PREFIX}:`))
    .some((key) => {
      const state = parsePersistedState(window.localStorage.getItem(key));
      return (
        state?.workspaceId === workspaceId &&
        Boolean(state.selectedTask) &&
        state.timerStatus !== 'idle'
      );
    });
}

export function useHasActivePersistedTimeTracker(workspaceId?: string) {
  const [hasActiveTimer, setHasActiveTimer] = useState(() =>
    hasActivePersistedTimeTracker(workspaceId),
  );

  useEffect(() => {
    const sync = () => {
      setHasActiveTimer(hasActivePersistedTimeTracker(workspaceId));
    };

    sync();

    if (typeof window === 'undefined') {
      return;
    }

    window.addEventListener('storage', sync);
    window.addEventListener(TIME_TRACKER_CHANGE_EVENT, sync);
    window.addEventListener(TIME_TRACKER_DISCARD_EVENT, sync);

    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener(TIME_TRACKER_CHANGE_EVENT, sync);
      window.removeEventListener(TIME_TRACKER_DISCARD_EVENT, sync);
    };
  }, [workspaceId]);

  return hasActiveTimer;
}
